import { Box, Text } from '@chakra-ui/react'

import type { DirectionType } from '../aiNodeMenu'

import { toaster } from '@app/components/ui/toaster'
import { useStore } from '@app/store'
import type { DomainNode } from '@app/types'
import { MetricsGenerateFromFormula } from '@graphql/documents/ai_assistant.graphql'
import type { MetricsGenerateFromFormulaMutation, MetricsGenerateFromFormulaMutationVariables } from '@graphql/queries'

interface FormulaSuggestionProps {
  formula: string
  node: DomainNode
  direction: DirectionType
  handle: 'top' | 'bottom' | 'left' | 'right'
  additionalInstructions?: string
  onSuccess?: () => void
  setLoading: (loading: boolean) => void
  loading: boolean
}

const FormulaSuggestion = ({
  formula,
  node,
  direction,
  handle,
  additionalInstructions,
  onSuccess,
  setLoading,
  loading
}: FormulaSuggestionProps) => {
  const actionMutation = useStore.use.actionMutation()

  const handleClick = async () => {
    setLoading(true)
    try {
      const result = await actionMutation<
        MetricsGenerateFromFormulaMutation,
        MetricsGenerateFromFormulaMutationVariables
      >(MetricsGenerateFromFormula, {
        nodeId: node.nodeId,
        formula,
        direction,
        handle,
        additionalInstructions
      })
      if (result.error) {
        if (result.error.networkError) {
          toaster.create({ title: 'Error', description: result.error.networkError.message, type: 'error' })
        } else {
          const errors = result.error.graphQLErrors.map((err) => err.message).join(', ')
          toaster.create({ title: 'Error', description: errors, type: 'error' })
        }
      } else if (result.data?.metricsGenerateFromFormula?.success) {
        toaster.create({ title: 'Success', description: 'Metrics generated successfully', type: 'success' })
        if (onSuccess) {
          onSuccess()
        }
      }
    } catch (err) {
      toaster.create({ title: 'Error', description: err.message, type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      p={2}
      borderWidth={1}
      borderRadius="sm"
      mb={2}
      fontFamily="mono"
      bgColor="bg.subtle"
      cursor={loading ? 'not-allowed' : 'pointer'}
      _hover={{ bgColor: loading ? 'bg.muted' : 'bg.subtle' }}
      onClick={loading ? undefined : handleClick}
      opacity={loading ? 0.5 : 1}
    >
      <Text fontSize="xs">{formula}</Text>
    </Box>
  )
}

export default FormulaSuggestion
